import React from "react";
import styled, { keyframes } from "styled-components";
import Button from "../Button";
import { colors, weights, radius, shadow } from "../theme";
import { fadeUp } from "../Styles";

const CHECKLIST = [
  "Confirmation email sent to registered address",
  "Payment receipt will be issued shortly",
];

const Confirmation = ({ onReset, referenceNumber }) => (
  <Wrap>
    <Card>
      <CheckRing>✓</CheckRing>
      <Title>Booking Confirmed!</Title>
      <Desc>
        Your hearing room is confirmed. A confirmation receipt and session
        details will be sent to your registered email shortly.
      </Desc>
      <RefCard>
        <div>
          <RefLabel>Booking Reference Number</RefLabel>
          <RefNum>
            {referenceNumber
              ? referenceNumber
              : `NPAC-${Date.now().toString().slice(-6)}`}
          </RefNum>
        </div>
      </RefCard>
      <ChecklistRow>
        {CHECKLIST.map((item, i) => (
          <CheckItem key={i}>
            <CheckDot>
              <svg viewBox="0 0 10 10" fill="none">
                <path
                  d="M2 5L4 7.5L8.5 2.5"
                  stroke={colors.teal}
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CheckDot>
            {item}
          </CheckItem>
        ))}
      </ChecklistRow>
      <Button variant="primary" size="lg" fullWidth onClick={onReset}>
        Book Another Room
      </Button>
    </Card>
  </Wrap>
);

export default Confirmation;

const scaleIn = keyframes`
  0%   { transform: scale(0) rotate(-15deg); opacity: 0; }
  60%  { transform: scale(1.1) rotate(4deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const Wrap = styled.div`
  max-width: 560px;
  margin: 60px auto;
  padding: 0 20px 60px;
  animation: ${fadeUp} 0.5s ease;
`;

const Card = styled.div`
  background: ${colors.white};
  border-radius: ${radius.xxl};
  padding: clamp(32px, 6vw, 52px) clamp(24px, 5vw, 44px);
  box-shadow: ${shadow.xl};
  border: 1.5px solid ${colors.border};
  text-align: center;
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(
      90deg,
      ${colors.teal},
      ${colors.tealLight},
      ${colors.navy}
    );
  }
`;

const CheckRing = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.teal}, ${colors.tealLight});
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 34px;
  box-shadow:
    0 8px 30px rgba(0, 131, 140, 0.35),
    0 0 0 10px rgba(0, 131, 140, 0.1);
  animation: ${scaleIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both;
`;

const Title = styled.h2`
  font-family: openSans-SemiBold, sans-serif;
  font-weight: 800;
  font-size: clamp(22px, 4vw, 28px);
  color: ${colors.navy};
  letter-spacing: -0.02em;
  margin-bottom: 12px;
`;

const Desc = styled.p`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 14px;
  color: ${colors.textSub};
  line-height: 1.7;
  margin-bottom: 28px;
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
`;

const RefCard = styled.div`
  background: linear-gradient(
    135deg,
    ${colors.surface} 0%,
    ${colors.navyGhost} 100%
  );
  border-radius: ${radius.lg};
  padding: 18px 22px;
  margin-bottom: 28px;
  border: 1.5px solid ${colors.navyFog};
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const RefLabel = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 10.5px;
  font-weight: ${weights.semiBold};
  color: ${colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.09em;
  margin-bottom: 5px;
`;

const RefNum = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-weight: 800;
  font-size: 20px;
  color: ${colors.navy};
  letter-spacing: 0.04em;
`;

const ChecklistRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 28px;
  text-align: left;
`;

const CheckItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  color: ${colors.textSub};
`;

const CheckDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${colors.tealGhost};
  border: 1.5px solid ${colors.tealFog};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg {
    width: 10px;
    height: 10px;
  }
`;
